import { useInfiniteQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FieldErrorMessage } from '@features/campaigns/builder/components/FormFields/common/FieldErrorMessage/FieldErrorMessage';
import { EnhancedDropdown } from '@features/campaigns/builder/components/FormFields/FieldTypes.ts';
import { BillingFormFieldTypes } from '@features/campaigns/details/components/FormFields/FieldTypes.ts';
import { QUERY_KEYS } from '@features/campaigns/details/consts/queryKeys.ts';
import { fetchClientBrands } from '@features/campaigns/details/services/formFields.ts';
import { DropdownWithSearch } from '@features/campaigns/shared/components/FormFields/Dropdown/components/DropdownWithSearch/DropdownWithSearch';
import { getNextParamFn } from '@features/campaigns/shared/components/FormFields/Dropdown/utils/getNextParamFn';
import fieldStyles from '@shared/components/FormFields/FormFields.module.scss';
import { i18nNameSpace } from '@shared/consts/i18n.ts';
import { useDebounce } from '@shared/hooks/useDebounce';

interface FieldBrandProps {
  clientFieldValue: string;
  clearSelectedValuesHandler: () => void;
  restoredInputValue?: string;
  wasFormRestored: boolean;
  disabled: boolean;
  showCode: boolean;
}

export function FieldBrand({
  clientFieldValue,
  clearSelectedValuesHandler,
  restoredInputValue,
  wasFormRestored,
  disabled,
  showCode,
}: FieldBrandProps) {
  const componentRef = useRef<EnhancedDropdown>(null);
  const [inputValue, setInputValue] = useState<string | undefined>(undefined);
  const [searchQueryValue, setSearchQueryValue] = useState<string | undefined>(undefined);
  const [previousClientFieldValue, setPreviousClientFieldValue] = useState(clientFieldValue);
  const debouncedQueryValue = useDebounce(searchQueryValue, 500);
  const { control } = useFormContext();

  const { t } = useTranslation(i18nNameSpace.CAMPAIGNS);

  const isFieldBlocked = !clientFieldValue;

  if (wasFormRestored && previousClientFieldValue !== clientFieldValue) {
    setPreviousClientFieldValue(clientFieldValue);
    setInputValue(undefined);
    setSearchQueryValue(undefined);
  }

  const { data, fetchNextPage, isFetching } = useInfiniteQuery({
    queryKey: [QUERY_KEYS.ONEFLIGHT_CLIENT_BRANDS, clientFieldValue, debouncedQueryValue],
    queryFn: (queryParams) => {
      const search = debouncedQueryValue;
      const page = queryParams?.pageParam?.page || 1;
      return fetchClientBrands(clientFieldValue, { pageParam: { page, search } });
    },
    initialPageParam: { page: 1, search: debouncedQueryValue },
    getNextPageParam: getNextParamFn(debouncedQueryValue),
    enabled: !isFieldBlocked,
    refetchOnMount: false,
    gcTime: 0,
  });

  return (
    <div className={fieldStyles.fieldWrapper}>
      <label htmlFor={BillingFormFieldTypes.BRAND} className={fieldStyles.fieldLabel}>
        {t('builder.formFields.brand.label')}
      </label>
      <Controller
        control={control}
        name={BillingFormFieldTypes.BRAND}
        render={({ field, formState: { errors } }) => {
          return (
            <>
              <DropdownWithSearch
                fieldName={BillingFormFieldTypes.BRAND}
                componentRef={componentRef}
                disabled={isFieldBlocked || disabled}
                placeholder={t('builder.formFields.brand.placeholder')}
                data={data}
                isFetching={isFetching}
                inputValue={inputValue}
                setInputValue={setInputValue}
                restoredInputValue={restoredInputValue}
                setSearchQueryValue={setSearchQueryValue}
                clearSelectedValuesHandler={clearSelectedValuesHandler}
                fieldController={field}
                fetchNextPage={fetchNextPage}
                showCode={showCode}
              />
              {errors?.[BillingFormFieldTypes.BRAND] && (
                <FieldErrorMessage fieldErrorData={errors} fieldName={BillingFormFieldTypes.BRAND} />
              )}
            </>
          );
        }}
      />
    </div>
  );
}
